import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/* ------------------------------------------------------------------ *
 * Theme and density are per-user, not per-business: the same buyer
 * wants Hall on the market floor and Office at the desk regardless of
 * which tenant they are looking at. Both are written to <html> as data
 * attributes so every token resolves through CSS with no re-render.
 * ------------------------------------------------------------------ */

export type Theme = 'hall' | 'office' | 'auto';
export type Density = 'compact' | 'comfortable' | 'market';
/** Where the navigation docks — the Windows-taskbar move. */
export type Dock = 'left' | 'right' | 'top' | 'bottom';

interface AppearanceState {
  theme: Theme;
  density: Density;
  dock: Dock;
  navCollapsed: boolean;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setDock: (dock: Dock) => void;
  toggleNav: () => void;
}

const apply = (theme: Theme, density: Density, dock: Dock): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset['theme'] = theme;
  document.documentElement.dataset['density'] = density;
  document.documentElement.dataset['dock'] = dock;
};

export const useAppearance = create<AppearanceState>()(
  persist(
    (set, get) => ({
      theme: 'hall',
      density: 'comfortable',
      dock: 'left',
      navCollapsed: false,
      setTheme: (theme) => {
        apply(theme, get().density, get().dock);
        set({ theme });
      },
      setDensity: (density) => {
        apply(get().theme, density, get().dock);
        set({ density });
      },
      setDock: (dock) => {
        apply(get().theme, get().density, dock);
        set({ dock });
      },
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
    }),
    {
      name: 'prody.appearance',
      onRehydrateStorage: () => (state) => {
        if (state) apply(state.theme, state.density, state.dock);
      },
    },
  ),
);

/**
 * A buyer opening the app on a phone at 04:00 should not have to find
 * a settings menu. First run on a small touch screen defaults to the
 * market density; an explicit choice is never overridden.
 */
export const applyContextualDefaults = (): void => {
  const state = useAppearance.getState();
  const stored = localStorage.getItem('prody.appearance');
  if (stored) return;
  const isTouchPhone =
    window.matchMedia('(pointer: coarse)').matches && window.innerWidth < 820;
  if (isTouchPhone) state.setDensity('market');
};
